<script setup>
  import { computed } from 'vue';
  import { useWindAlertStore } from '@/stores/WindAlertStore';
  import Toggle from './controls/Toggle.vue';
  import Icon from '@/components/icons/Icon.vue';

  const windAlertStore = useWindAlertStore();

  const props = defineProps({
    type: {
      type: String,
      required: true,
    },
    device: {
      type: Object,
      default: null,
    },
    kind: {
      type: String,
      default: 'website',
      validator: v => ['website', 'app'].includes(v),
    },
    subText: {
      type: String,
      default: '',
    },
  });

  const emits = defineEmits(['edit', 'setup', 'toggle']);

  const displayLabel = computed(() =>
    windAlertStore.deviceTypeMap[props.type]?.typeDisplay || props.type
  );

  const icon = computed(() => {
    const lookupType = props.device?.type || props.type;
    return windAlertStore.deviceTypeMap[lookupType]?.icon || 'iphone';
  });

  const isClickable = computed(() => !!props.device || props.kind === 'website');

  const rowPadding = computed(() =>
    props.device && props.kind === 'app' ? 'jwx-p-block-lg' : 'jwx-p-block-md'
  );

  function handleClick() {
    if (props.device) emits('edit', props.device);
    else emits('setup', props.type);
  }
</script>

<template>
  <li class="jwx-table-view__row">
    <button
      v-if="isClickable"
      type="button"
      class="jwx-table-view__action-row"
      :class="rowPadding"
      @click="handleClick"
    >
      <div class="jwx-table-view__row-icon">
        <Icon :name="icon" />
      </div>
      <div class="jwx-table-view__row-main-content">
        <p class="jwx-table-view__row-label jwx-font-weight-400">{{ displayLabel }}</p>
        <p v-if="device && subText" class="jwx-table-view__row-text">{{ subText }}</p>
      </div>
      <div class="jwx-table-view__action-row-detail">
        <Toggle
          v-if="device"
          :id="`device-toggle-${device.id}`"
          :modelValue="device.enabled === true"
          :hideLabelText="true"
          size="small"
          @click.stop
          @update:modelValue="(v) => emits('toggle', device, v)"
        >
          Toggle Device
        </Toggle>
        <span v-else class="jwx-chip" data-selected="true">
          <span class="jwx-chip__text jwx-chip__text--strong">Setup</span>
        </span>
        <Icon name="chevron-right" />
      </div>
    </button>
    <div v-else class="jwx-table-view__action-row jwx-p-block-md">
      <div class="jwx-table-view__row-icon">
        <Icon :name="icon" />
      </div>
      <div class="jwx-table-view__row-main-content">
        <p class="jwx-table-view__row-label jwx-font-weight-400">{{ displayLabel }}</p>
      </div>
      <div class="jwx-table-view__action-row-detail">
        <span class="jwx-chip jwx-chip--neutral">
          <span class="jwx-chip__text jwx-chip__text--strong">Setup on Device</span>
        </span>
      </div>
    </div>
  </li>
</template>
